<template>
    <div>
        <loading
        :active.sync="loading"
        :can-cancel="false"
        :is-full-page="true"
        :height="150"
        :width="150"
        :color="'#FFFF00'"
        :background-color="'#000000'"
        :opacity="0.5"
        ></loading>

        <v-container style="max-width:100%;overflow-x:hidden;">
            <v-form>
                <!-- Title -->
                <v-row class="px-4 pt-2 pb-1">
                    <h2>{{ this.pageTitle }}<span class="page_title_value pl-2">{{ this.header.companyname }}</span></h2>
                </v-row>

                <!-- Back button -->
                <v-col class="mt-n2 mb-2 px-4">
                    <v-btn color="warning" class="white--text" @click="backButton()">
                        <v-icon size="16" class="mr-1">mdi-arrow-left</v-icon>
                        {{$t('button.back')}}
                    </v-btn>
                </v-col>

                <v-container id="scroll-target" class="px-4">

                    <!-- Header card: payout name + export buttons -->
                    <v-card flat class="summary-header-card mb-4">
                        <div class="summary-header-inner">
                            <div>
                                <div class="summary-payout-name">{{ this.header.payoutname }}</div>
                                <div class="summary-sub-title">
                                    {{ $t('payrollsymmarybycompanyreport.payrollsumforyear') }}
                                    <span class="blue--text font-weight-bold" v-if="this.data.formData.pyyear"> {{ this.data.formData.pyyear }}</span>
                                    <span v-if="this.data.formData.pymonth">, {{ $t('reportsparam.month') }} <span class="blue--text font-weight-bold">{{ this.data.formData.pymonth }}</span></span>
                                </div>
                            </div>
                            <div class="export-btns">
                                <button type="button" class="export-icon-btn export-icon-pdf" @click="printPDF" title="Export PDF">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h2a2 2 0 0 1 0 4H9v-4z"/><path d="M15 13h1a2 2 0 0 1 0 4h-1"/><line x1="15" y1="17" x2="17" y2="17"/></svg>
                                    PDF
                                </button>
                                <button type="button" class="export-icon-btn export-icon-csv" @click="getCSV" title="Export CSV">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
                                    CSV
                                </button>
                            </div>
                        </div>
                    </v-card>

                    <!-- EARNING -->
                    <div class="section-label blue--text">{{ $t('payrollsymmarybycompanyreport.earning') }}</div>
                    <v-data-table :headers="dataHeader" :items="dataItemsEarning" :options="tableOpt" hide-default-footer class="summary-table">
                        <template v-slot:body>
                            <tbody>
                                <tr v-for="item in dataItemsEarning" :key="item.id">
                                    <td class="col-no">{{ item.id }}</td>
                                    <td class="col-item">{{ item.payitemcode }}</td>
                                    <td class="col-amount">{{ item.amount }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                    <div class="subtotal-bar">
                        <span class="subtotal-label">{{ $t('payrollsymmarybycompanyreport.subtotalforearning') }}</span>
                        <span class="subtotal-value">{{ earningTotal }}</span>
                    </div>

                    <!-- DEDUCTION -->
                    <div class="section-label blue--text mt-6">{{ $t('payrollsymmarybycompanyreport.deduction') }}</div>
                    <v-data-table :headers="dataHeader" :items="dataItemsDeduction" :options="tableOpt" hide-default-footer class="summary-table">
                        <template v-slot:body>
                            <tbody>
                                <tr v-for="item in dataItemsDeduction" :key="item.id">
                                    <td class="col-no">{{ item.id }}</td>
                                    <td class="col-item">{{ item.payitemcode }}</td>
                                    <td class="col-amount">{{ item.amount }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                    <div class="subtotal-bar">
                        <span class="subtotal-label">{{ $t('payrollsymmarybycompanyreport.subtotalfordeduction') }}</span>
                        <span class="subtotal-value">{{ deductionTotal }}</span>
                    </div>

                    <!-- STAFF STATUTORY -->
                    <div class="section-label blue--text mt-6">{{ $t('payrollsymmarybycompanyreport.staffstatdeduct') }}</div>
                    <v-data-table :headers="dataHeader" :items="dataItemsStaffS" :options="tableOpt" hide-default-footer class="summary-table">
                        <template v-slot:body>
                            <tbody>
                                <tr v-for="item in dataItemsStaffS" :key="item.id">
                                    <td class="col-no">{{ item.id }}</td>
                                    <td class="col-item">{{ item.payitemcode }}</td>
                                    <td class="col-amount">{{ item.amount }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                    <div class="subtotal-bar">
                        <span class="subtotal-label">{{ $t('payrollsymmarybycompanyreport.subtotalforstaffstatutorydeduction') }}</span>
                        <span class="subtotal-value">{{ staffdeductionTotal }}</span>
                    </div>

                    <!-- COMPANY STATUTORY -->
                    <div class="section-label blue--text mt-6">{{ $t('payrollsymmarybycompanyreport.compstatdeduct') }}</div>
                    <v-data-table :headers="dataHeader" :items="dataItemsCompanyS" :options="tableOpt" hide-default-footer class="summary-table">
                        <template v-slot:body>
                            <tbody>
                                <tr v-for="item in dataItemsCompanyS" :key="item.id">
                                    <td class="col-no">{{ item.id }}</td>
                                    <td class="col-item">{{ item.payitemcode }}</td>
                                    <td class="col-amount">{{ item.amount }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                    <div class="subtotal-bar">
                        <span class="subtotal-label">{{ $t('payrollsymmarybycompanyreport.subtotalforcompanystatutorydeduction') }}</span>
                        <span class="subtotal-value">{{ companydeductionTotal }}</span>
                    </div>

                    <!-- NET PAY -->
                    <div class="netpay-bar mt-4">
                        <span class="netpay-label">{{ $t('previewpayroll.netpay') }}</span>
                        <span class="netpay-value">{{ netTotal }}</span>
                    </div>

                </v-container>
            </v-form>
        </v-container>
    </div>
</template>

<script>

// import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Loading from "vue-loading-overlay";


export default {

    components: {
        Loading,
    },

    data() {
        return {
            publicPath: process.env.BASE_URL,
            rules: {
                required: value => !!value || this.$t('reportsparam.required'),
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                }
            },

            pageTitle: this.$t('payrollsymmarybycompanyreport.payrollsummarybycompany'),
            pageTitleValue: '',
            
            data: {
                formData: {
                    pyyear: this.$route.params.formData.pyyear,
                    pymonth: this.$route.params.formData.pymonth,
                    poid: this.$route.params.formData.poid,
                    compnyid: this.$route.params.formData.compnyid
                },

                parameters: {
                    brchid: this.$route.params.param.branchid,
                    //compnyid: this.$route.params.param.companyid,
                    deptid: this.$route.params.param.departmentid,
                    emptypeid: this.$route.params.param.employmenttypeid,
                    gradeid: this.$route.params.param.gradeid,
                    staffid: this.$route.params.param.staffid,
                },
            },

            header: {
                companyname: null,
                payoutname: null,
            },

            dataHeader: [
                {text: this.$t('columns.no'),          width: '20px',  value: 'salhdid'},
                {text: this.$t('setuppayitem.payitem'),    width: '400px', value: 'payitemdesc'},
                {text: this.$t('setuppersonpayrolldirectassignment.amount'),      width: '50px',  value: 'amount', align: 'end'}
            ],

            tableOpt: {
                itemsPerPage: -1
            },

            earningTotal:  null,
            deductionTotal:  null,
            staffdeductionTotal: null,
            companydeductionTotal: null,
            netTotal: null,

            dataItemsEarning: [],
            dataItemsDeduction: [],
            dataItemsStaffS: [],
            dataItemsCompanyS: [],

            checkearning:  false,
            checkdeduction:  false,
            checkstaffdeduction: false,
            checkcompanydeduction: false,
            loading: false,
        }
    },

    methods: {

        backButton(){
            this.$router.push({name: 'parametercompanysummary'});
        },

        loadEarning() {
            const vm = this;

            return this.$store.dispatch('post', {url: `/payrollsummarycompanyEarning/${this.$store.getters.userId}`, payload: this.data})
            .then(function (res) {
                vm.dataItemsEarning = res.data.rec;
                let getamount = []

                for (let i = 0; i < vm.dataItemsEarning.length; i++) {
                    vm.dataItemsEarning[i].id = i + 1;
                    getamount.push(parseFloat(vm.dataItemsEarning[i].amount.replace(/,/g, '')));
                }
                

                let getearning = getamount.reduce(function(a, b) {
                    return a + b;
                }, 0);

                getearning = getearning.toFixed(2)

                vm.earningTotal = getearning.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")

                vm.checkearning = true
            })
            .catch(function (err) {
                vm.errMsg = err;
            });
        },

        loadDeduction() {
            const vm = this;

            return this.$store.dispatch('post', {url: `/payrollsummarycompanyDeduction/${this.$store.getters.userId}`, payload: this.data})
            .then(function (res) {
                vm.dataItemsDeduction = res.data.rec;
                let getamount = []

                for (let i = 0; i < vm.dataItemsDeduction.length; i++) {
                    vm.dataItemsDeduction[i].id = i + 1;
                    getamount.push(parseFloat(vm.dataItemsDeduction[i].amount.replace(/,/g, '')));
                }

                let getdeduction = getamount.reduce(function(a, b) {
                    return a + b;
                }, 0);

                getdeduction = getdeduction.toFixed(2)

                vm.deductionTotal = getdeduction.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")

                vm.checkdeduction = true
            })
            .catch(function (err) {
                vm.errMsg = err;
            });
        },

        loadStaffS() {
            const vm = this;

            return this.$store.dispatch('post', {url: `/payrollsummarycompanyStaffS/${this.$store.getters.userId}`, payload: this.data})
            .then(function (res) {
                vm.dataItemsStaffS = res.data.rec;
                let getamount = []

                for (let i = 0; i < vm.dataItemsStaffS.length; i++) {
                    vm.dataItemsStaffS[i].id = i + 1;
                    getamount.push(parseFloat(vm.dataItemsStaffS[i].amount.replace(/,/g, '')));
                }

                let getstaffdeduction = getamount.reduce(function(a, b) {
                    return a + b;
                }, 0);

                getstaffdeduction = getstaffdeduction.toFixed(2)

                vm.staffdeductionTotal = getstaffdeduction.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")

                vm.checkstaffdeduction = true
            })
            .catch(function (err) {
                vm.errMsg = err;
            });
        },

        loadCompanyS() {
            const vm = this;

            return this.$store.dispatch('post', {url: `/payrollsummarycompanyCompanyS/${this.$store.getters.userId}`, payload: this.data})
            .then(function (res) {
                vm.dataItemsCompanyS = res.data.rec;
                let getamount = []

                for (let i = 0; i < vm.dataItemsCompanyS.length; i++) {
                    vm.dataItemsCompanyS[i].id = i + 1;
                    getamount.push(parseFloat(vm.dataItemsCompanyS[i].amount.replace(/,/g, '')));
                }

                let getcompanydeduction = getamount.reduce(function(a, b) {
                    return a + b;
                }, 0);

                getcompanydeduction = getcompanydeduction.toFixed(2)

                vm.companydeductionTotal = getcompanydeduction.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")

                vm.checkcompanydeduction = true
            })
            .catch(function (err) {
                vm.errMsg = err;
            });
        },
        
        loadNetTotal() {

            return new Promise((resolve) => {
                setTimeout(() => {
                    const vm = this
                    if (vm.checkearning == true && vm.checkdeduction == true &&
                        vm.checkstaffdeduction == true && vm.checkcompanydeduction == true) {
                        const vm = this;

                        let getamount = [
                            parseFloat(vm.earningTotal.replace(/,/g, '')),
                            parseFloat(vm.deductionTotal.replace(/,/g, '')),
                            parseFloat(vm.staffdeductionTotal.replace(/,/g, '')),
                        ]

                        let getnettotal = getamount.reduce(function(a, b) {
                            return a - b;
                        });

                        getnettotal = getnettotal.toFixed(2)

                        vm.netTotal = getnettotal.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }

                    resolve();
                }, 1000);
            });
        },

        loadCompany() {
            const vm = this;
            return this.$store.dispatch('get', {url: '/company/' + vm.data.formData.compnyid})
            .then(res => {
                let company = res.data.rec[0];
                vm.header.companyname = company.compnyname
            })
            .catch(err => {
                vm.isErr = !vm.isErr;
                vm.errMsg = err;
                vm.$emit('error', {isErr: vm.isErr, errMsg: vm.errMsg});
            });
        },

        loadPayoutControl() {
            const vm = this;

            return this.$store.dispatch('get', {url: "/payoutctrl/" + vm.data.formData.poid})
            .then((res) => {
                let payout = res.data.rec[0];
                vm.header.payoutname = payout.payoutdesc
            })
            .catch((err) => {
                vm.isErr = !vm.isErr;
                vm.errMsg = err;
            });
        },

        printPDF() {

            const doc = new jsPDF({
                orientation: 'landscape'
            })

            var head = [
                { title: this.$t('columns.no'),         dataKey: 'salhdid' },
                { title: this.$t('payrollsymmarybycompanyreport.type'),       dataKey: 'type' },
                { title: this.$t('setuppayitem.payitem'),   dataKey: 'payitemdesc' },
                { title: this.$t('setuppersonpayrolldirectassignment.amount'),     dataKey: 'amount' },
                { title: this.$t('bankpaymentlistingreport.total'),      dataKey: 'total' },
                { title: this.$t('payrollsymmarybycompanyreport.nettotal'),  dataKey: 'nettotal' },
            ]

            let body = []

            for (var i = 0; i < this.dataItemsEarning.length; i++) {
                body.push({
                    salhdid : this.dataItemsEarning[i].id,
                    type : "Earning",
                    payitemdesc : this.dataItemsEarning[i].payitemcode,
                    amount : this.dataItemsEarning[i].amount
                })
            }
            body.push({
                total : this.earningTotal,
            })

            for (i = 0; i < this.dataItemsDeduction.length; i++) {
                body.push({
                    salhdid : this.dataItemsDeduction[i].id,
                    type : "Deduction",
                    payitemdesc : this.dataItemsDeduction[i].payitemcode,
                    amount : this.dataItemsDeduction[i].amount
                })
            }
            body.push({
                total : this.deductionTotal,
            })

            for (i = 0; i < this.dataItemsStaffS.length; i++) {
                body.push({
                    salhdid : this.dataItemsStaffS[i].id,
                    type : "Staff Statutory",
                    payitemdesc : this.dataItemsStaffS[i].payitemcode,
                    amount : this.dataItemsStaffS[i].amount
                })
            }
            body.push({
                total : this.staffdeductionTotal,
            })

            for (i = 0; i < this.dataItemsCompanyS.length; i++) {
                body.push({
                    salhdid : this.dataItemsCompanyS[i].id,
                    type : "Company Statutory",
                    payitemdesc : this.dataItemsCompanyS[i].payitemcode,
                    amount : this.dataItemsCompanyS[i].amount
                })
            }
            body.push({
                total : this.companydeductionTotal,
            })
            
            body.push({
                nettotal : this.netTotal,
            })
            
            doc.text(this.$t('payrollsymmarybycompanyreport.payrollsummarybycompany') + this.header.companyname + '-' +  this.header.payoutname, 10, 10)
            doc.autoTable(head, body)
            doc.save(this.$t('payrollsymmarybycompanyreport.payrollsummarybycompany') + this.header.companyname + '-' + this.header.payoutname + '.pdf')
        },

        objectToCSV(data) {
            const csvRows = []

            // get header
            const headers = Object.keys(data[0])
            csvRows.push(headers.join(','))

            // loop over the rows
            for (const row of data) {
                const values = headers.map(header => {
                    const escaped = ('' + row[header]).replace(/"/g, '\\"')
                    return `"${escaped}"`
                })
                csvRows.push(values.join(','))
            }
            return csvRows.join('\n')
        },

        download(data) {
            const blob = new Blob([decodeURIComponent('%ef%bb%bf'), data], { type: 'text/csv'})
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')

            a.setAttribute('hidden', '')
            a.setAttribute('href', url)
            a.setAttribute('download', this.$t('payrollsymmarybycompanyreport.payrollsummarybycompany') + this.header.companyname + '-' + this.header.payoutname + '.csv')

            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        },

        async getCSV() {
            let itemE = this.dataItemsEarning
            let itemD = this.dataItemsDeduction
            let itemS = this.dataItemsStaffS
            let itemC = this.dataItemsCompanyS
            let data = []

            for (var i = 0; i < itemE.length; i++) {

            data.push({
                [`${this.$t('columns.no')}`]: itemE[i].id,
                [`${this.$t('payrollsymmarybycompanyreport.type')}`]: "Earning",
                [`${this.$t('setuppayitem.payitem')}`]: itemE[i].payitemcode,
                 [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: itemE[i].amount,
                 [`${this.$t('bankpaymentlistingreport.total')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
                })
            }

            data.push({
                [`${this.$t('columns.no')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.type')}`]: '',
                [`${this.$t('setuppayitem.payitem')}`]: '',
                 [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: '',
                 [`${this.$t('bankpaymentlistingreport.total')}`]: this.earningTotal,
                [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
            })

            for ( i = 0; i < itemD.length; i++) {
                data.push({
                    [`${this.$t('columns.no')}`]: itemD[i].id,
                    [`${this.$t('payrollsymmarybycompanyreport.type')}`]: "Deduction",
                    [`${this.$t('setuppayitem.payitem')}`]: itemD[i].payitemcode,
                     [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: itemD[i].amount,
                     [`${this.$t('bankpaymentlistingreport.total')}`]: '',
                    [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
                })
            }

            data.push({
                [`${this.$t('columns.no')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.type')}`]: '',
                [`${this.$t('setuppayitem.payitem')}`]: '',
                 [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: '',
                 [`${this.$t('bankpaymentlistingreport.total')}`]: this.deductionTotal,
                [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
            })

            for ( i = 0; i < itemS.length; i++) {
                data.push({
                    [`${this.$t('columns.no')}`]: itemS[i].id,
                    [`${this.$t('payrollsymmarybycompanyreport.type')}`]: "Staff Statutory Deduction",
                    [`${this.$t('setuppayitem.payitem')}`]: itemS[i].payitemcode,
                     [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: itemS[i].amount,
                     [`${this.$t('bankpaymentlistingreport.total')}`]: '',
                    [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
                })
            }

            data.push({
                [`${this.$t('columns.no')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.type')}`]: '',
                [`${this.$t('setuppayitem.payitem')}`]: '',
                 [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: '',
                 [`${this.$t('bankpaymentlistingreport.total')}`]: this.staffdeductionTotal,
                [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
            })

            for ( i = 0; i < itemC.length; i++) {
                data.push({
                   [`${this.$t('columns.no')}`]: itemC[i].id,
                    [`${this.$t('payrollsymmarybycompanyreport.type')}`]: "Company Statutory Deduction",
                    [`${this.$t('setuppayitem.payitem')}`]: itemC[i].payitemcode,
                     [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: itemC[i].amount,
                     [`${this.$t('bankpaymentlistingreport.total')}`]: '',
                    [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
                })
            }

            data.push({
                [`${this.$t('columns.no')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.type')}`]: '',
                [`${this.$t('setuppayitem.payitem')}`]: '',
                 [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: '',
                 [`${this.$t('bankpaymentlistingreport.total')}`]: this.companydeductionTotal,
                [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: ''
            })

            data.push({
                [`${this.$t('columns.no')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.type')}`]: '',
                 [`${this.$t('setuppayitem.payitem')}`]: '',
                 [`${this.$t('setuppersonpayrolldirectassignment.amount')}`]: '',
                [`${this.$t('bankpaymentlistingreport.total')}`]: '',
                [`${this.$t('payrollsymmarybycompanyreport.nettotal')}`]: this.netTotal
            })

            let csvData = this.objectToCSV(data)
            this.download(csvData)
        },

        

    },

    async mounted() {
        this.loading = true;
        try {
            await this.loadEarning();
            await this.loadDeduction();
            await this.loadStaffS();
            await this.loadCompanyS();
            await this.loadNetTotal();
            await this.loadCompany();
            await this.loadPayoutControl();
        } finally {
            this.loading = false;
        }
    }
    
}
</script>

<style scoped>
/* Header card */
.summary-header-card {
    background: #F8FAFF;
    border: 1px solid #E8EDF5;
    border-radius: 12px;
    padding: 16px 20px;
}
.summary-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.summary-payout-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2563EB;
    margin-bottom: 4px;
}
.summary-sub-title {
    font-size: 0.88rem;
    color: #64748B;
}
.export-btns { display: flex; gap: 8px; align-items: center; }
.export-icon-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 8px; border: none;
    font-size: 0.8rem; font-weight: 600; cursor: pointer;
    font-family: inherit; transition: all 0.15s;
}
.export-icon-pdf { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; }
.export-icon-pdf:hover { background: #DC2626; color: #fff; }
.export-icon-csv { background: #ECFDF5; color: #059669; border: 1px solid #A7F3D0; }
.export-icon-csv:hover { background: #059669; color: #fff; }

/* Section label */
.section-label {
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 8px 8px 4px 8px;
}

/* Table */
.summary-table { border-radius: 8px; overflow: hidden; }
.summary-table .col-no   { width: 60px;  padding-left: 16px !important; color: #64748B; font-size: 0.82rem; }
.summary-table .col-item { font-size: 0.88rem; font-weight: 500; }
.summary-table .col-amount {
    text-align: right !important;
    padding-right: 24px !important;
    font-size: 0.88rem;
    font-family: inherit;
    font-weight: 500;
}

/* Subtotal bar */
.subtotal-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    background: #F1F5F9;
    border-radius: 0 0 8px 8px;
    padding: 10px 24px;
    gap: 32px;
}
.subtotal-label {
    font-size: 0.88rem;
    font-weight: 600;
    color: #475569;
}
.subtotal-value {
    font-size: 1rem;
    font-weight: 700;
    color: #1E293B;
    font-family: inherit;
    min-width: 120px;
    text-align: right;
}

/* Net Pay bar */
.netpay-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    background: linear-gradient(135deg, #FFF7ED, #FFEDD5);
    border: 1px solid #FED7AA;
    border-radius: 10px;
    padding: 14px 24px;
    gap: 32px;
    margin-bottom: 24px;
}
.netpay-label {
    font-size: 1rem;
    font-weight: 700;
    color: #92400E;
}
.netpay-value {
    font-size: 1.25rem;
    font-weight: 800;
    color: #92400E;
    font-family: inherit;
    min-width: 140px;
    text-align: right;
}
</style>